import { IChildRoute, IRoute, routes } from 'components/mainLayout/routes';
import keys from 'lodash/keys';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { genUuid } from 'utils/genUuid';

import { ADMIN_BASE_PATH } from '@app/common/helpers';

interface IBreadcrumbsProps {
  className?: string;
}

const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({ className }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const params = useParams();

  const convertPathname = useMemo(() => {
    let result = '';

    const pathnameLength = pathname.length;

    if (pathname.charAt(pathnameLength - 1) === '/') {
      result = pathname.substring(pathnameLength - 1, 0);
    } else {
      result = pathname;
    }

    return result;
  }, [pathname]);

  const handleFindRouteByPathname = (path: string): string | void => {
    let routeLabel = '';
    let foundRouteLabel = false;

    const findRouteByPathNameRecursive = (path: string, routes: IRoute[] | IChildRoute[]): void | string => {
      for (const route of routes) {
        const routePath = route.path.charAt(0) === '/' ? route.path.replace('/', '') : route.path;
        if (routePath.startsWith(path)) {
          foundRouteLabel = true;

          return (routeLabel = route.label);
        } else {
          if ('children' in route && route.children?.length) {
            findRouteByPathNameRecursive(path, route.children);
            if (foundRouteLabel) break;
          }
        }
      }
    };

    findRouteByPathNameRecursive(path, routes);

    return routeLabel;
  };

  const handleCheckParamsItem = useCallback(
    (path: string): boolean => {
      // INFO: check if path is existing in params
      return keys(params).every((paramItem) => params[paramItem] !== path);
    },
    [params]
  );

  const generatePath = useCallback(
    (path: string) => {
      path = `/${path.replace('/', '')}`;
      if (convertPathname.startsWith(ADMIN_BASE_PATH) && !path.startsWith(ADMIN_BASE_PATH)) path = ADMIN_BASE_PATH + (path === '/' ? '' : path);

      return path;
    },
    [convertPathname]
  );

  return (
    <div className={['font-hl-mulish my-5 text-hl-black-70 xl:my-10', className].join(' ')}>
      <ol className='flex flex-wrap items-center justify-start'>
        {convertPathname.split('/').map((path) =>
          handleCheckParamsItem(path) ? (
            <Fragment key={genUuid()}>
              {path ? <span className='mx-2 block'>{'>'}</span> : <></>}
              <li className='text-base font-normal capitalize last:pointer-events-none last:text-hl-primary'>
                <Link to={generatePath(path)} className='cursor-pointer hover:text-hl-primary'>
                  {t(`${handleFindRouteByPathname(path)}`)}
                </Link>
              </li>
            </Fragment>
          ) : (
            <Fragment key={genUuid()}></Fragment>
          )
        )}
      </ol>
    </div>
  );
};

export default Breadcrumbs;
